import React from 'react';
import AvatarIcon from '@/components/AvatarIcon';
import {
  BatchIcon,
  BatchStackIcon,
  SwitchHorizontalIcon,
  XeroWordmarkIcon
} from '@/components/Icons';
import { ReactComponent as EmergeBatchAvatarBackground } from '@/assets/EmergeBatchAvatarBackground.svg';
import {
  ScheduledBankPaymentGroupTypeEnum,
  ScheduledPaymentIntervalTypeEnum,
  TScheduledBankPaymentGroupType
} from '@/features/payments/types/BatchPayment';
import { batchPaymentsPage } from '@/utils/translations';
import { PaymentRowKindEnum } from '../types/ManagePayments';
import { TPaymentPresentationStrategy } from '../types/PaymentKindStrategy';
import PaymentAvatar from '../components/PaymentAvatar';
import ScheduledPaymentModal from '../components/ScheduledPaymentModal';
import ScheduledTransferModal from '../components/ScheduledTransferModal';
import BatchModal from '../components/BatchModal';

/**
 * The actual per-kind `TPaymentPresentationStrategy` implementations - deliberately kept in their
 * own file, separate from data/paymentPresentationStrategies.ts's registry/lookup ("process"), so
 * adding a new kind's rendering never means editing that process file's own logic, only adding a
 * new export here and one registration line there.
 *
 * Each `renderAvatar` builds its own PaymentAvatar frame from scratch (background, glyph, badge)
 * rather than PaymentAvatar switching on a passed-in variant itself - PaymentAvatar stays a plain,
 * kind-agnostic circular frame (see components/PaymentAvatar), and every visual decision about
 * "what does this kind's icon look like" lives here, fully customisable per kind without touching
 * that shared component.
 */

const BATCH_TYPE_LABEL: Record<TScheduledBankPaymentGroupType, string> = {
  xero: batchPaymentsPage.types.xero,
  emerge: batchPaymentsPage.types.emerge
};

export const batchPresentationStrategy: TPaymentPresentationStrategy = {
  kind: PaymentRowKindEnum.BATCH,
  renderAvatar: (row) => {
    if (row.type === ScheduledBankPaymentGroupTypeEnum.XERO) {
      return (
        <PaymentAvatar background="bg-brand-xero-blue">
          <div className="absolute left-[26.57%] top-[58%] h-[13.13%] w-[43.77%]">
            <XeroWordmarkIcon className="h-full w-full text-white" />
          </div>
          <div className="absolute left-[36%] top-[18%] h-[34%] w-[30%]">
            <BatchStackIcon className="h-full w-full text-white" />
          </div>
        </PaymentAvatar>
      );
    }

    if (row.type === ScheduledBankPaymentGroupTypeEnum.EMERGE) {
      return (
        <PaymentAvatar>
          <EmergeBatchAvatarBackground className="absolute inset-0 h-full w-full" />
          <div className="absolute left-[30%] top-[26%] h-[46%] w-[42%]">
            <BatchIcon className="h-full w-full text-emerge-navy" />
          </div>
        </PaymentAvatar>
      );
    }

    // Unexpected/missing batch type - fall back to the same initials avatar every other kind uses.
    return (
      <PaymentAvatar>
        <AvatarIcon key={row.name} name={row.name} variant="xs" className="h-full w-full" />
      </PaymentAvatar>
    );
  },
  renderTypeLabel: (row) => {
    switch (row.type) {
      case ScheduledBankPaymentGroupTypeEnum.EMERGE:
        return BATCH_TYPE_LABEL[ScheduledBankPaymentGroupTypeEnum.EMERGE];
      case ScheduledBankPaymentGroupTypeEnum.XERO:
        return BATCH_TYPE_LABEL[ScheduledBankPaymentGroupTypeEnum.XERO];
      default:
        return row.type ? BATCH_TYPE_LABEL[row.type] ?? row.type : '-';
    }
  },
  Modal: BatchModal
};

// Covers scheduled_payment and pending_scheduled_payment alike (see the registry in
// paymentPresentationStrategies.ts) - both render identically everywhere except the modal's own
// internal approve/reject gating (driven by `state`, not `kind` - see resolvePaymentActionVariant).
export const scheduledPaymentPresentationStrategy: TPaymentPresentationStrategy = {
  kind: PaymentRowKindEnum.SCHEDULED_PAYMENT,
  renderAvatar: (row) => (
    <PaymentAvatar>
      <AvatarIcon key={row.name} name={row.name} variant="xs" className="h-full w-full" />
    </PaymentAvatar>
  ),
  renderTypeLabel: (row) =>
    row.paymentIntervalType === ScheduledPaymentIntervalTypeEnum.ONE_OFF
      ? batchPaymentsPage.types.scheduled
      : batchPaymentsPage.types.recurringScheduled,
  Modal: ScheduledPaymentModal
};

export const scheduledTransferPresentationStrategy: TPaymentPresentationStrategy = {
  kind: PaymentRowKindEnum.SCHEDULED_TRANSFER,
  // No badge overlay for this one (see Figma: "Transfer / Large graphic" is a plain teal circle).
  renderAvatar: () => (
    <PaymentAvatar background="bg-emerge-teal" showBadge={false}>
      <div className="absolute left-1/2 top-1/2 h-[48%] w-[48%] -translate-x-1/2 -translate-y-1/2">
        <SwitchHorizontalIcon className="h-full w-full" />
      </div>
    </PaymentAvatar>
  ),
  // Always "Scheduled transfer", regardless of one-off vs recurring - unlike scheduled
  // payments/batches, this column doesn't distinguish the two for a transfer (see the Figma for
  // both the Scheduled and Action required tabs).
  renderTypeLabel: () => batchPaymentsPage.types.scheduledTransfer,
  Modal: ScheduledTransferModal
};
